/**
 * "Run report" CTA — queues an on-demand performance report for a project.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { LineChart, Play } from 'lucide-react';
import { toast } from 'sonner';
import type { Project, Report } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useRunReport } from '@/features/reports/use-run-report';

const ReportFormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  lookback_days: z
    .number({ invalid_type_error: 'Enter a number' })
    .int()
    .min(7)
    .max(90)
    .default(28),
});
type ReportFormValues = z.infer<typeof ReportFormSchema>;

export interface ReportCreateButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  navigateOnSuccess?: boolean;
}

export function ReportCreateButton({
  projects,
  defaultProjectId,
  label = 'Run report',
  buttonProps,
  navigateOnSuccess = true,
}: ReportCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <Play className="h-4 w-4 fill-current" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — reports summarize one site at a time."
      />
    );
  }

  return (
    <ReportDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      navigateOnSuccess={navigateOnSuccess}
    />
  );
}

function ReportDialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  navigateOnSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  navigateOnSuccess: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const run = useRunReport();
  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReportFormValues>({
    resolver: zodResolver(ReportFormSchema),
    defaultValues: {
      project_id: initialProjectId,
      lookback_days: 28,
    },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const lookback = watch('lookback_days');

  useEffect(() => {
    if (!projectId && projects.length === 1) {
      setValue('project_id', projects[0]!.id, { shouldValidate: false });
    }
  }, [projectId, projects, setValue]);

  const submit = handleSubmit(async (values) => {
    try {
      const end = new Date();
      const start = new Date(end.getTime() - values.lookback_days * 86400_000);
      const created = await run.mutateAsync({
        project_id: values.project_id,
        period_start: start.toISOString().slice(0, 10),
        period_end: end.toISOString().slice(0, 10),
        cadence: 'custom',
      });
      toast.success('Report queued', {
        description: `Last ${values.lookback_days} days`,
      });
      reset();
      onOpenChange(false);
      if (navigateOnSuccess) {
        await navigate({
          to: '/reports/$reportId',
          params: { reportId: (created as Report).id },
        });
      }
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      if (fieldErrors.project_id) {
        setError('project_id', { type: 'server', message: fieldErrors.project_id });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not run the report',
        fieldLabels: { project_id: 'Project' },
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<LineChart className="h-5 w-5 text-primary" />}
      title="Run a performance report"
      description="We'll pull Search Console / Analytics (when connected), spot anomalies, and write a plain-English summary for the period."
      submitLabel="Run report"
      submitting={isSubmitting || run.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger aria-invalid={errors.project_id ? true : undefined}>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField label="Period" error={errors.lookback_days?.message}>
        <Select
          value={String(lookback)}
          onValueChange={(v) =>
            setValue('lookback_days', Number(v), { shouldValidate: true })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="28">Last 28 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
    </FormDialog>
  );
}
